import type { PropsWithChildren } from 'react';
import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import type { GraphModalInstance } from '../../../components/GraphModal/GraphModal';
import { GraphModal } from '../../../components/GraphModal/GraphModal';
import { confirmationToast, failureToast } from '../../../components/Toast';
import type { DeleteResponseStatusFragment } from '../../api/message';
import { deleteMessage } from '../../api/message';
import { RemoveContent } from '../../Remove/RemoveContent';

const messages = defineMessages({
  title: { defaultMessage: 'Comment deletion', id: 'pVEPnA' },
  guide: {
    defaultMessage: 'Are you sure you want to delete your comment?',
    id: 'cpBUc1',
  },
  cancel: { defaultMessage: 'keep the comment', id: 'YYhaO7' },
  remove: { defaultMessage: 'delete', id: 'sznaQh' },
  confirmationLabel: { defaultMessage: 'the message has been deleted', id: 'hUXU/+' },
  failureLabel: { defaultMessage: 'Unable to delete the message', id: 'eb8URm' },
});

export type MessageRemoverProps = {
  messageId: string;
  threadId?: string;
  content: string;
  onRemove: (data: DeleteResponseStatusFragment) => void;
};
export const MessageRemover: React.FC<PropsWithChildren<MessageRemoverProps>> = ({
  messageId,
  content,
  onRemove,
  children,
}) => {
  const remove = useCallback(async () => {
    try {
      const data = await deleteMessage({ commentId: messageId });
      confirmationToast(<FormattedMessage {...messages.confirmationLabel} />);
      onRemove?.(data);
    } catch {
      failureToast(<FormattedMessage {...messages.failureLabel} />);
    }
  }, [onRemove, messageId]);

  return (
    <GraphModal
      maxWidth={400}
      lazyContent={(tip: GraphModalInstance) => (
        <RemoveContent
          messages={messages}
          cancel={() => tip?.hide()}
          remove={async () => {
            await remove();
            tip?.hide();
          }}
          content={content}
        />
      )}
      showPageOverlay
      forceModal
    >
      {children}
    </GraphModal>
  );
};
